import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ProfileModal from "../components/ProfileModal";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";
const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];

function Modal({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: "90vh" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none font-bold">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

export default function ResidentDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [data, setData] = useState(null);
  const [debtDetail, setDebtDetail] = useState(null);
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [managerContact, setManagerContact] = useState(null); // yönetici iletişim kartı

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData || userData.role !== "resident") { navigate("/login"); return; }
    setUser(userData);
    loadData(userData);
  }, [navigate]);

  const loadData = async (userData) => {
    try {
      const [invoicesRes, debtRes] = await Promise.all([
        api.get(`/payment-links/unit/${userData.unit_id}`),
        api.get(`/invoices/unit-debt/${userData.unit_id}`)
      ]);
      setData(invoicesRes.data.data);
      setDebtDetail(debtRes.data.data);

      // Yönetici iletişim kartı — sitesi backend token'dan çözer.
      try {
        const contactRes = await api.get('/tenants/my/manager-contact');
        if (contactRes.data?.data?.enabled) setManagerContact(contactRes.data.data);
      } catch (e) {
        // Kart gösterilemezse panel çalışmaya devam eder
        console.error('Yönetici iletişim bilgisi alınamadı:', e.response?.status);
      }
    } catch (e) {
      console.error("Veri yükleme hatası:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { localStorage.clear(); navigate("/login"); };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const invoices = data?.invoices || [];
  const filtered = invoices.filter(inv => filterStatus ? inv.status === filterStatus : true);
  const unpaidInvoices = invoices.filter(i => i.status === "unpaid");

  const totalDebt = parseFloat(debtDetail?.summary?.total_debt || 0);
  const totalBase = parseFloat(debtDetail?.summary?.total_base || 0);
  const totalFee = parseFloat(debtDetail?.summary?.total_fee || 0);
  const totalExempted = parseFloat(debtDetail?.summary?.total_exempted_fee || 0);

  // Ödeme linki token'ını ödenmemiş ilk faturadan çıkar
  const firstUnpaid = unpaidInvoices[0];
  const paymentLink = firstUnpaid?.payment_link;
  const payToken = paymentLink ? paymentLink.split("/").pop() : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-2xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Sakin Paneli</h1>
            <p className="text-sm text-gray-500">
              {data?.unit?.tenant_name} — Daire {data?.unit?.unit_number}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden sm:block cursor-pointer hover:underline" onClick={() => setShowProfileModal(true)}>{user?.full_name}</span>
            <button onClick={handleLogout} className="bg-red-500 text-white px-3 py-1.5 rounded text-sm">Çıkış</button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* BORÇ ÖZET KARTI */}
        {unpaidInvoices.length > 0 ? (
          <div className="bg-white border border-red-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="bg-red-50 px-5 py-4 border-b border-red-100">
              <h2 className="font-bold text-red-800 text-lg">🔴 Toplam Borcunuz</h2>
            </div>
            <div className="px-5 py-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Aidat borcu ({unpaidInvoices.length} fatura)</span>
                <span>₺{totalBase.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
              </div>
              {totalFee > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>Gecikme faizi</span>
                  <span>+₺{totalFee.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {totalExempted > 0 && (
                <div className="flex justify-between text-sm text-purple-500">
                  <span>📅 Muafiyet (uygulanmadı)</span>
                  <span className="line-through">₺{totalExempted.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold text-red-800 border-t pt-3 mt-1">
                <span>Ödenecek Toplam</span>
                <span>₺{totalDebt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <button
                onClick={() => setShowDebtModal(true)}
                className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-3 rounded-xl text-sm"
              >
                📋 Borç Detayı
              </button>
              {payToken ? (
                <button
                  onClick={() => navigate(`/pay/${payToken}`)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl text-sm"
                >
                  💳 Hemen Öde
                </button>
              ) : (
                <button disabled className="flex-1 bg-gray-100 text-gray-400 font-bold py-3 rounded-xl text-sm cursor-not-allowed">
                  💳 Ödeme Linki Yok
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-2">✅</div>
            <p className="font-bold text-green-800 text-lg">Tüm faturalarınız ödenmiş</p>
            <p className="text-green-600 text-sm mt-1">Aktif borcunuz bulunmuyor.</p>
          </div>
        )}

        {/* YÖNETİCİ İLETİŞİM */}
        {managerContact && (
          <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-lg shrink-0">📇</div>
              <div className="min-w-0">
                <p className="text-[11px] text-gray-400 uppercase tracking-wide leading-none">Site Yöneticisi</p>
                <p className="font-semibold text-gray-800 text-sm mt-1 leading-none">{managerContact.name}</p>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                {managerContact.phone && (
                  <a href={`tel:${managerContact.phone.replace(/\s/g, "")}`}
                     className="flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg font-medium">
                    <span>📞</span><span className="hidden sm:inline">{managerContact.phone}</span><span className="sm:hidden">Ara</span>
                  </a>
                )}
                {managerContact.email && (
                  <a href={`mailto:${managerContact.email}`}
                     className="flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg font-medium">
                    <span>✉️</span><span className="hidden sm:inline">E-posta</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* FATURA LİSTESİ */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Fatura Geçmişi</h2>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm">
              <option value="">Tümü ({invoices.length})</option>
              <option value="unpaid">Ödenmemiş ({unpaidInvoices.length})</option>
              <option value="paid">Ödendi ({invoices.filter(i => i.status === "paid").length})</option>
            </select>
          </div>
          {filtered.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">Fatura bulunamadı.</p>
          ) : (
            <div className="divide-y">
              {filtered.map(inv => (
                <div key={inv.id} className={`px-5 py-4 ${inv.status === "unpaid" && new Date(inv.due_date) < new Date() ? "bg-red-50" : ""}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-gray-800">
                          {MONTHS[inv.period_month - 1]} {inv.period_year}
                        </span>
                        {inv.status === "paid" ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ Ödendi</span>
                        ) : new Date(inv.due_date) < new Date() ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            Gecikmiş {inv.months_overdue > 0 ? `(${inv.months_overdue} ay)` : ""}
                          </span>
                        ) : (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Bekliyor</span>
                        )}
                        {inv.is_period_exempt && (
                          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">📅 Faiz muaf</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex gap-3 flex-wrap">
                        <span>Aidat: ₺{parseFloat(inv.base_amount).toFixed(2)}</span>
                        {inv.discount_amount && parseFloat(inv.discount_amount) > 0 && (
                          <span className="text-green-600">İndirim: -₺{parseFloat(inv.discount_amount).toFixed(2)}</span>
                        )}
                        {inv.status === "unpaid" && parseFloat(inv.calculated_fee || 0) > 0 && (
                          <span className="text-orange-600">Faiz: +₺{parseFloat(inv.calculated_fee).toFixed(2)}</span>
                        )}
                        {inv.status === "paid" && inv.paid_at && (
                          <span className="text-green-600">
                            {new Date(inv.paid_at).toLocaleDateString("tr-TR")} — {inv.payment_method || "ödendi"}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="font-bold text-gray-800 ml-3 flex-shrink-0">
                      ₺{parseFloat(inv.status === "unpaid" ? (inv.calculated_total || inv.total_amount) : inv.total_amount).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      <ProfileModal
        show={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        onUpdate={(updatedUser) => setUser(prev => ({ ...prev, ...updatedUser }))}
      />

      {/* BORÇ DETAYI MODAL */}
      <Modal show={showDebtModal} onClose={() => setShowDebtModal(false)} title="📋 Borç Detayı">
        {debtDetail && (
          <>
            {debtDetail.items.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4">Ödenmemiş fatura yok.</p>
            ) : (
              <>
                <div className="border rounded-xl overflow-hidden mb-4">
                  <div className="bg-gray-50 px-3 py-2 grid grid-cols-4 gap-1 text-xs font-semibold text-gray-500 uppercase">
                    <span>Dönem</span><span>Gecikme</span><span>Faiz</span><span className="text-right">Toplam</span>
                  </div>
                  {debtDetail.items.map(item => (
                    <div key={item.invoice_id} className={`px-3 py-3 border-t grid grid-cols-4 gap-1 text-sm items-center ${item.is_period_exempt ? "bg-purple-50" : ""}`}>
                      <div>
                        <span className="font-medium">{MONTHS[item.period_month - 1]} {item.period_year}</span>
                        {item.is_period_exempt && <span className="block text-xs text-purple-600">📅 Muaf</span>}
                      </div>
                      <span className={item.months_overdue > 0 ? "text-red-600 font-semibold" : "text-gray-400"}>
                        {item.months_overdue > 0 ? `${item.months_overdue} ay` : "—"}
                      </span>
                      <span>
                        {item.is_period_exempt
                          ? parseFloat(item.exempted_fee) > 0
                            ? <span className="line-through text-gray-400 text-xs">₺{parseFloat(item.exempted_fee).toFixed(2)}</span>
                            : <span className="text-gray-300">—</span>
                          : parseFloat(item.fee_amount) > 0
                            ? <span className="text-orange-600">+₺{parseFloat(item.fee_amount).toFixed(2)}</span>
                            : <span className="text-gray-300">—</span>
                        }
                      </span>
                      <span className="font-bold text-right">₺{parseFloat(item.total_amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 mb-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Aidat Borcu</span>
                    <span>₺{parseFloat(debtDetail.summary.total_base).toFixed(2)}</span>
                  </div>
                  {totalFee > 0 && (
                    <div className="flex justify-between text-sm text-orange-600">
                      <span>Gecikme Faizi</span>
                      <span>+₺{totalFee.toFixed(2)}</span>
                    </div>
                  )}
                  {totalExempted > 0 && (
                    <div className="flex justify-between text-sm text-purple-500">
                      <span>Muafiyet (uygulanmadı)</span>
                      <span className="line-through">₺{totalExempted.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-gray-900 border-t pt-2">
                    <span>Ödenecek Toplam</span>
                    <span className="text-red-700">₺{totalDebt.toFixed(2)}</span>
                  </div>
                </div>

                {payToken && (
                  <button
                    onClick={() => { setShowDebtModal(false); navigate(`/pay/${payToken}`); }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl"
                  >
                    💳 Hemen Öde
                  </button>
                )}
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}